/**
 * ThreadPanel — engagement-wide OR work-item threads.
 *
 * Renders messages (newest first, with pagination), lets participants
 * post new messages, edit own messages within 15 minutes, and soft-
 * delete with a mandatory reason.
 *
 * No financial fields are read, computed or displayed.
 */
import { useEffect, useMemo, useState } from "react";
import { PaperPlaneRight, PencilSimple, Trash } from "@phosphor-icons/react";

import { api, formatApiError } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { toast } from "sonner";


function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.floor((now - then) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return iso.substring(0, 10);
}


export default function ThreadPanel({ scope, engagementId, workItemId }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [posting, setPosting] = useState(false);
    const perms = new Set(user?.permissions || []);
    const canPost = perms.has("project:comment") || user?.role === "super_admin";

    const listUrl = scope === "work_item"
        ? `/projects/work-item/${workItemId}/messages`
        : `/projects/engagement/${engagementId}/messages`;

    const postUrl = scope === "work_item"
        ? `/projects/work-item/${workItemId}/message`
        : `/projects/engagement/${engagementId}/message`;

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`${listUrl}?limit=100`);
            setMessages(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, engagementId, workItemId]);

    const submit = async () => {
        if (!body.trim()) return;
        setPosting(true);
        try {
            const key = `op-msg-${scope}-${workItemId || engagementId}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(postUrl, { body: body.trim() }, { headers: { "Idempotency-Key": key } });
            setBody("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPosting(false);
        }
    };

    const softDelete = async (m) => {
        const reason = window.prompt("Reason for soft-deletion (visible in audit log):");
        if (!reason || reason.trim().length < 3) return;
        try {
            await api.delete(
                `/projects/work-item/${workItemId}/message/${m.id}?reason=${encodeURIComponent(reason.trim())}`,
                { headers: { "If-Match": String(m._version) } },
            );
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <section data-testid={`op-thread-${scope}`}>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
                {scope === "engagement" ? "Engagement Thread" : "Work Item Thread"}
            </h2>
            <div className="border border-slate-200 rounded-md">
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                    {loading ? (
                        <div className="p-3 text-sm text-slate-500">Loading...</div>
                    ) : messages.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500" data-testid="op-thread-empty">
                            No messages yet.
                        </div>
                    ) : (
                        messages.map((m) => (
                            <div key={m.id} className="p-3 hover:bg-slate-50" data-testid={`op-msg-${m.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-slate-500">
                                        <span className="font-medium text-slate-700">
                                            {m.author_snapshot?.name || m.author_snapshot?.email || m.author_user_id}
                                        </span>
                                        {" · "}
                                        {timeAgo(m.created_at)}
                                        {m.edit_history?.length > 0 && (
                                            <span className="ml-2 italic text-slate-400">(edited)</span>
                                        )}
                                    </div>
                                    {scope === "work_item" && m.author_user_id === user?.id && !m.soft_deleted_at && (
                                        <button
                                            onClick={() => softDelete(m)}
                                            className="text-slate-400 hover:text-rose-600"
                                            title="Soft delete"
                                            data-testid={`op-msg-delete-${m.id}`}
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                <div className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">
                                    {m.soft_deleted_at ? (
                                        <span className="italic text-slate-400">
                                            [message soft-deleted]
                                            {m.soft_deleted_reason && ` — ${m.soft_deleted_reason}`}
                                        </span>
                                    ) : (
                                        m.body
                                    )}
                                </div>
                                {m.mentions?.length > 0 && !m.soft_deleted_at && (
                                    <div className="mt-1 text-xs text-slate-500">
                                        Mentions: {m.mentions.map((mm) => mm.user_name || mm.user_id).join(", ")}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
                {canPost && (
                    <div className="border-t border-slate-100 p-3 flex items-start gap-2">
                        <Textarea
                            data-testid="op-msg-composer"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Post an update, question, or reply..."
                            rows={2}
                            className="flex-1"
                        />
                        <Button onClick={submit} disabled={posting} data-testid="op-msg-post-btn">
                            <PaperPlaneRight className="w-4 h-4 mr-1" />
                            {posting ? "Posting..." : "Post"}
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
